import { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../../../lib/i18n';
import { messageDir } from '../../../../lib/message-direction';
import { handleModalEscape, pushModalEscape } from '../../../../lib/modal-escape';
import { LinkifiedText } from '../../../LinkifiedText';
import { BackButton, Button, StatusBadge, ScrollArea, Typography } from '../../ui';
import { AgentConversationCard } from '../AgentConversationCard';
import { TaskActivityTimeline } from '../TaskActivityTimeline';
import { TaskFilesSection } from '../TaskFilesSection';
import { TaskSentOutreachSection } from '../TaskSentOutreachSection';
import { useTaskDetailModal } from '../use-task-detail-modal';
import { CancelTaskConfirm } from '../CancelTaskConfirm';
import taskStyles from '../TaskDetailsPanel.module.css';
import styles from './TaskDetailMobile.module.css';

// The narrow task-details drill-in (Figma 12175-102348): a full-screen sheet rather than a centered
// card — a back control over the title + status badge + description on the standard grey surface, the
// agent-activity timeline on a white sunken band that fills the rest, and a pinned full-width
// "Resolve with {agent}" CTA. Always full-screen (it is only mounted below `useNarrow`, so unlike the
// shared Modal's `fullScreenOnSmall` there is no centered-card range to guard against). Behaviour, copy
// and analytics are the shared useTaskDetailModal — only the layout differs from the desktop modal.
export function TaskDetailMobile({ taskId }: { taskId: string }) {
  const vm = useTaskDetailModal(taskId);
  const { t } = useTranslation();

  const sheetRef = useRef<HTMLDivElement>(null);
  const entranceRef = useRef<Animation | null>(null);
  const closingRef = useRef(false);
  // Read `close` through a ref so a fresh handler each render doesn't re-register the escape stack.
  const closeRef = useRef(vm.close);
  closeRef.current = vm.close;

  // Dismiss with a slide-down, then unmount. The openModal/closeModal slot owns the sheet's lifetime,
  // so it would otherwise vanish instantly — we play the exit first and call close() on finish. Held
  // in a ref (kept fresh each render) so the mount-once Escape listener always calls the latest one.
  // Reentrancy-guarded so Escape + a tap can't both fire. Reduced motion (or no element) closes at once.
  const requestCloseRef = useRef<() => void>(() => {});
  requestCloseRef.current = () => {
    if (closingRef.current) {
      return;
    }
    closingRef.current = true;
    const el = sheetRef.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      closeRef.current();
      return;
    }
    // Start from wherever the sheet visually is (mid-open close doesn't snap to the top first); WAAPI
    // commits the start keyframe atomically, and `fill: 'forwards'` holds it off-screen until unmount.
    const from = getComputedStyle(el).transform;
    entranceRef.current?.cancel();
    const exit = el.animate([{ transform: from === 'none' ? 'translateY(0)' : from }, { transform: 'translateY(100%)' }], {
      duration: 260,
      easing: 'cubic-bezier(0.4, 0, 1, 1)',
      fill: 'forwards',
    });
    exit.onfinish = () => closeRef.current();
    exit.oncancel = () => closeRef.current();
  };

  // Slide up from the bottom on open, like a pushed screen. Driven by the Web Animations API rather than
  // a mount-time CSS transition: it commits the off-screen start keyframe atomically on the first frame
  // (no flash, no rAF/state race), runs on the compositor, and skips entirely under reduced-motion.
  useEffect(() => {
    const el = sheetRef.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    const anim = el.animate([{ transform: 'translateY(100%)' }, { transform: 'translateY(0)' }], {
      duration: 300,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    });
    entranceRef.current = anim;
    return () => anim.cancel();
  }, []);

  // Claim Escape for this sheet the same way the DS Modal does (capture-phase, innermost-first) so it
  // closes — with the slide-down — without unwinding any panel underneath.
  useEffect(() => {
    const pop = pushModalEscape(() => requestCloseRef.current());
    const onKey = (e: KeyboardEvent) => handleModalEscape(e);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      pop();
    };
  }, []);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div ref={sheetRef} className={styles.sheet} role="dialog" aria-modal="true" aria-labelledby="task-detail-title">
      <div className={styles.topBar}>
        <BackButton onClick={() => requestCloseRef.current()} />
      </div>

      {vm.kind === 'error' ? (
        <div className={styles.stateRegion}>{t('tasks.detail.notFound')}</div>
      ) : vm.kind === 'loading' ? (
        <SheetShell
          title={
            <div className={styles.skelStack}>
              <Shimmer w="85%" h={22} />
              <Shimmer w="55%" h={22} />
            </div>
          }
          badge={<Shimmer w={96} h={20} r={8} />}
          desc={
            <div className={styles.skelStack}>
              <Shimmer w="100%" h={14} />
              <Shimmer w="80%" h={14} />
            </div>
          }
          activity={<TimelineSkeleton />}
          footer={<Shimmer w="100%" h={42} r={8} />}
        />
      ) : (
        <SheetShell
          titleId="task-detail-title"
          title={<Typography variant="h2">{vm.title}</Typography>}
          badge={<StatusBadge status={vm.task.status} detail />}
          desc={
            vm.description ? (
              <Typography variant="p2" component="p" className={styles.desc} dir={messageDir(vm.description)}>
                <LinkifiedText text={vm.description} />
              </Typography>
            ) : null
          }
          activity={
            <>
              <TaskActivityTimeline
                agentId={vm.task.agentId}
                agentName={vm.agentName}
                steps={vm.task.steps}
                currentStep={vm.currentStep}
                statusVariant={vm.statusVariant}
                isLive={vm.isLive}
              />
              <TaskSentOutreachSection outreaches={vm.sentOutreaches} />
              <TaskFilesSection files={vm.files} onOpen={vm.onOpenFile} />
              <AgentConversationCard taskId={taskId} dashboardBuild={!!vm.task.isDashboardBuild} />
              <CancelTaskConfirm flow={vm.cancel} />
            </>
          }
          footer={
            <div className={styles.footerActions}>
              {/* The sheet's back chevron is its dismissal, so unlike the desktop modal there is no
                  "Close" to disambiguate here — only the cancel action joins the pinned CTA, stacked
                  above it in the secondary skin like the footer's other secondary action. */}
              {vm.canCancel && (
                <Button fullWidth size="large" variant="outlined" color="destructive" onClick={vm.onCancel}>
                  {t('tasks.cancel.action')}
                </Button>
              )}
              <Button fullWidth size="large" onClick={vm.cta.onClick}>
                {vm.cta.label}
              </Button>
            </div>
          }
        />
      )}
    </div>,
    document.body,
  );
}

// The sheet's body layout — the same regions whether loading or ready, so the skeleton and the real
// content occupy identical structure and the swap between them never reflows.
function SheetShell({
  titleId,
  title,
  badge,
  desc,
  activity,
  footer,
}: {
  titleId?: string;
  title: ReactNode;
  badge: ReactNode;
  desc: ReactNode;
  activity: ReactNode;
  footer: ReactNode;
}) {
  return (
    <>
      <ScrollArea className={styles.scroll}>
        <div className={styles.header}>
          <div className={styles.title} id={titleId}>
            {title}
          </div>
          <div className={styles.statusStack}>
            {badge}
            {desc}
          </div>
        </div>
        <div className={styles.activity}>{activity}</div>
      </ScrollArea>
      <div className={styles.footer}>{footer}</div>
    </>
  );
}

function Shimmer({ w, h, r = 6 }: { w: number | string; h: number; r?: number }) {
  return <div className="orion-shimmer" style={{ width: w, height: h, borderRadius: r }} />;
}

// The activity placeholder — the same agent row + left-ruled step list the ready timeline draws.
function TimelineSkeleton() {
  return (
    <div className={taskStyles.activity}>
      <div className={taskStyles.agent}>
        <Shimmer w={15} h={15} r={8} />
        <Shimmer w={80} h={14} />
      </div>
      <div className={taskStyles.timeline}>
        {[0, 1, 2].map((i) => (
          <div key={i} className={taskStyles.step}>
            <Shimmer w={16} h={16} r={8} />
            <Shimmer w={i === 1 ? '70%' : '88%'} h={14} />
          </div>
        ))}
      </div>
    </div>
  );
}
